import React, { memo, useCallback } from 'react';

import { Box } from '@mui/material';
import PropTypes from 'prop-types';

import { SEARCH_CATEGORY_LABELS } from '../../UniversalSearch.constants';
import { resultsContainerStyles } from '../../UniversalSearch.styles';

import CategoryHeader from './components/CategoryHeader';
import EmptyState from './components/EmptyState';
import NoResults from './components/NoResults';
import SearchResultItem from './components/SearchResultItem';
import { CategoryOrder } from './SearchResults.constants';

const SearchResults = ({
  query,
  groupedResults,
  flatResults,
  flatSuggestedItems,
  selectedIndex,
  onSelect,
  onHoverItem,
  shortcutEntries,
  suggestedItems,
  recentItems,
  onClearRecent,
}) => {
  const hasResults = !!flatResults.length;
  const hasQuery = !!query?.trim();

  const handleMouseEnterFactory = useCallback(index => () => onHoverItem(index), [onHoverItem]);

  if (!hasQuery) {
    return (
      <EmptyState
        flatSuggestedItems={flatSuggestedItems}
        recentItems={recentItems}
        selectedIndex={selectedIndex}
        shortcutEntries={shortcutEntries}
        suggestedItems={suggestedItems}
        onClearRecent={onClearRecent}
        onHoverItem={onHoverItem}
        onSelect={onSelect}
      />
    );
  }

  if (!hasResults) {
    return <NoResults query={query} />;
  }

  const runningIndex = { current: 0 };

  return (
    <Box sx={resultsContainerStyles}>
      {CategoryOrder.map(category => {
        const items = groupedResults[category];

        if (!items?.length) {
          return null;
        }

        return (
          <Box key={category}>
            <CategoryHeader label={SEARCH_CATEGORY_LABELS[category]} />
            {items.map(item => {
              const currentIndex = runningIndex.current;
              runningIndex.current += 1;

              return (
                <SearchResultItem
                  isSelected={currentIndex === selectedIndex}
                  item={item}
                  key={item.id}
                  onClick={onSelect}
                  onMouseEnter={handleMouseEnterFactory(currentIndex)}
                />
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

SearchResults.propTypes = {
  flatResults: PropTypes.arrayOf(PropTypes.object).isRequired,
  flatSuggestedItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  groupedResults: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
  selectedIndex: PropTypes.number.isRequired,
  shortcutEntries: PropTypes.arrayOf(PropTypes.object).isRequired,
  suggestedItems: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
  onHoverItem: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  recentItems: PropTypes.arrayOf(PropTypes.object),
  query: PropTypes.string,
  onClearRecent: PropTypes.func,
};

export default memo(SearchResults);
